/**
 * Number / date / cell formatters shared by the builder preview and the
 * portal production render. Single source of truth for how numbers,
 * currencies, percentages, dates, and bilingual JSON cells display.
 *
 * Locale resolution goes through ``i18n.language`` of the consumer's
 * ``react-i18next`` instance (wired via ``setI18nInstance``). Helpers
 * stay i18n-instance-agnostic so the package can ship without
 * importing a specific app's ``@/i18n`` module.
 */

import { getActiveLocale, getI18n, toLang, type Lang } from "./i18n-binding";
import { SUPPORTED_LOCALES } from "./i18n-resolve";

const LOCALE_MAP: Record<Lang, string> = {
  en: "en-US",
  es: "es-AR",
  pt: "pt-BR",
};

function intlLocale(): string {
  try {
    const lang = getI18n().language || "en";
    return LOCALE_MAP[toLang(lang)];
  } catch {
    return "en-US";
  }
}

// ── Intl.NumberFormat cache (review #56 #9) ──────────────────────────
// `Intl.NumberFormat` construction is expensive (locale data lookup)
// and `formatCellValue` sits on hot paths — table cells AND every
// `NumberTween` animation frame. Cache instances per (locale, options).
// Bounded by construction: the option sets are a small fixed vocabulary
// (the formats below) × 2 locales.
const NUMBER_FORMAT_CACHE = new Map<string, Intl.NumberFormat>();

function cachedNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let nf = NUMBER_FORMAT_CACHE.get(key);
  if (nf === undefined) {
    nf = new Intl.NumberFormat(locale, options);
    NUMBER_FORMAT_CACHE.set(key, nf);
  }
  return nf;
}

/** BCP-47 tag of the APP language ("es-AR" / "en-US"), for components
 *  that format numbers themselves (KPI cards). The browser's locale is
 *  not the app's: a Spanish UI in an English browser must still print
 *  "33,1 %". */
export function appNumberLocale(): string {
  return intlLocale();
}

/** A number in the app language, e.g. ``formatNumberLocale(1062)`` →
 *  "1.062" in ES / "1,062" in EN. Cached formatter (hot path). */
export function formatNumberLocale(
  value: number,
  options: Intl.NumberFormatOptions = {},
): string {
  return cachedNumberFormat(intlLocale(), options).format(value);
}

/** ISO calendar date, e.g. "2026-07-14" — used by the table cell
 *  renderers to style date cells (right-aligned, tabular-nums, muted).
 *  Formerly lived in ``semantic-cells.tsx``; the value-based badge /
 *  dot-pill system that file hosted was removed on user feedback
 *  (2026-07-14 — auto-styling by cell VALUE reads inconsistent with
 *  real data). If a specific column ever needs semantic color, the
 *  right path is an opt-in per-column ``render`` variant in the
 *  report config (like ``render: "trend"``) — never value detection. */
export function isIsoDateString(value: unknown): value is string {
  return (
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
  );
}

export type ValueFormat =
  | "number"
  | "percent"
  | "currency"
  | "compact"
  | "text";

/** Format a numeric value according to the dashboard convention. */
export function formatValue(
  value: number | string | null | undefined,
  format: ValueFormat = "number",
  options: { compact?: boolean; decimals?: number; fraction?: boolean } = {},
): string {
  if (value === null || value === undefined || value === "") return "—";

  if (format === "text") return String(value);

  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);

  const locale = intlLocale();

  if (format === "percent") {
    // Inputs may be 0..1 (fraction) or 0..100 (already a percent). The
    // dashboard convention in hp-portal is fractions for SQL aggregates,
    // so we treat anything in [-1, 1] as a fraction. Larger values are
    // displayed as-is with a `%` suffix — unless the caller knows the
    // value IS a fraction (`fraction: true`): a price gap of 1.13 is
    // +113%, not 1.1%, and the guess cannot tell the two apart.
    const isFraction = options.fraction ?? Math.abs(num) <= 1;
    return cachedNumberFormat(locale, {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: options.decimals ?? 1,
    }).format(isFraction ? num : num / 100);
  }

  if (format === "currency") {
    return cachedNumberFormat(locale, {
      style: "currency",
      currency: "USD",
      notation: options.compact ? "compact" : "standard",
      maximumFractionDigits: options.decimals ?? (options.compact ? 1 : 0),
    }).format(num);
  }

  if (format === "compact") {
    return cachedNumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: options.decimals ?? 1,
    }).format(num);
  }

  return cachedNumberFormat(locale, {
    maximumFractionDigits: options.decimals ?? 0,
  }).format(num);
}

/** Format a JS Date or ISO string as a relative date (e.g. "5 minutes ago"). */
export function formatRelative(input: Date | string): string {
  const date = typeof input === "string" ? new Date(input) : input;
  const ms = Date.now() - date.getTime();
  const sec = Math.round(ms / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);

  const rtf = new Intl.RelativeTimeFormat(intlLocale(), { numeric: "auto" });
  if (sec < 60) return rtf.format(-sec, "second");
  if (min < 60) return rtf.format(-min, "minute");
  if (hr < 24) return rtf.format(-hr, "hour");
  if (day < 30) return rtf.format(-day, "day");
  return new Intl.DateTimeFormat(intlLocale(), {
    dateStyle: "medium",
  }).format(date);
}

/** Format a JS Date or ISO string as a short absolute date (e.g. "Apr 14, 2026").
 *
 * Plain ``YYYY-MM-DD`` strings are interpreted as LOCAL calendar dates, not
 * UTC midnight — otherwise users in negative timezone offsets see "28 feb"
 * when they picked "1 mar". Full ISO timestamps with a time component
 * remain timezone-aware and go through the normal ``Date`` parser.
 */
export function formatDate(input: Date | string): string {
  let date: Date;
  if (typeof input === "string") {
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(input);
    if (dateOnly) {
      const [y, m, d] = input.split("-").map(Number) as [number, number, number];
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(input);
    }
  } else {
    date = input;
  }
  return new Intl.DateTimeFormat(intlLocale(), {
    dateStyle: "medium",
  }).format(date);
}

// ── hp-portal-compat formatters ──────────────────────────
// Used by the dashboard renderers (KPICard, DataTable, GenericChart).

export type DataFormat = "currency" | "percent" | "number" | "text";

/** Compact number for chart axes / tooltips (e.g. 1.2M, 350K). */
// ── Divisa declarada: `format: "currency:CLP"` ───────────────
//
// La divisa viaja DENTRO del `format` a propósito. Es el único campo de
// formato que ya cruza todo el recorrido (config → engine → los dos
// serializadores → chart-props / table-columns → acá) como string
// libre, así que declararla aquí no agrega un campo nuevo a seis
// archivos ni un tipo que se pueda olvidar en uno de los dos
// serializadores — el modo de falla que este repo ya conoce.
//
// `currency` a secas sigue siendo USD, así que ningún reporte publicado
// cambia de divisa.
const CURRENCY_FMT_RE = /^currency(?::([A-Za-z]{3}))?$/;

interface CurrencyFmt {
  /** El código ISO a usar. */
  code: string;
  /** `true` cuando el autor lo declaró explícitamente. */
  declared: boolean;
}

/** `"currency"` → USD implícito · `"currency:CLP"` → CLP declarado ·
 *  cualquier otra cosa → `null` (no es un formato de moneda). */
export function parseCurrencyFormat(
  fmt: DataFormat | string | null | undefined,
): CurrencyFmt | null {
  if (typeof fmt !== "string") return null;
  const m = CURRENCY_FMT_RE.exec(fmt.trim());
  if (!m) return null;
  const declared = m[1] ? m[1].toUpperCase() : null;
  return { code: declared ?? "USD", declared: declared !== null };
}

/** Opciones de `Intl` para una divisa.
 *
 *  El `currencyDisplay` es la decisión que importa y no es cosmética:
 *
 *   * **Declarada** → símbolo corto (`$279.990`). El autor se tomó el
 *     trabajo de decir en qué moneda reporta, o sea que TODO el reporte
 *     está en esa moneda y repetirla en cada celda es ruido.
 *   * **Sin declarar** → la divisa VA explícita (`US$ 279.990`). Nadie
 *     dijo nada, así que el default es USD — y un `$` a secas frente a
 *     un lector chileno se lee como pesos, o sea 300x el valor real.
 *     Ese es exactamente el número mentiroso que no se permite. */
function currencyIntlOpts(c: CurrencyFmt): Intl.NumberFormatOptions {
  return {
    style: "currency",
    currency: c.code,
    maximumFractionDigits: 0,
    ...(c.declared ? { currencyDisplay: "narrowSymbol" as const } : {}),
  };
}

/** El símbolo de una divisa en el locale activo, para los ejes que
 *  arman la escala a mano. */
function currencySymbol(c: CurrencyFmt): string {
  try {
    const parts = new Intl.NumberFormat(intlLocale(), {
      ...currencyIntlOpts(c),
      maximumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((x) => x.type === "currency")?.value ?? "$";
  } catch {
    return "$";
  }
}

export function formatCompactNumber(val: number): string {
  if (Math.abs(val) >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
  if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `${(val / 1e3).toFixed(0)}K`;
  return val.toFixed(0);
}

/** Compact currency for chart axes (e.g. $1.2M).
 *
 *  La ESCALA se arma a mano y no con `notation: "compact"` de `Intl`:
 *  medido, `Intl` en `es-AR` pierde la escala B y rinde 1.234.567.890
 *  como `$1234,6 M`, que en un eje es ilegible. El SÍMBOLO sí sale de
 *  `Intl`, así que una divisa declarada no termina rotulada con el `$`
 *  de otra. */
export function formatCompactCurrency(
  val: number,
  fmt?: DataFormat | string | null,
): string {
  const sym = currencySymbol(parseCurrencyFormat(fmt) ?? { code: "USD", declared: false });
  if (Math.abs(val) >= 1e9) return `${sym}${(val / 1e9).toFixed(1)}B`;
  if (Math.abs(val) >= 1e6) return `${sym}${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `${sym}${(val / 1e3).toFixed(0)}K`;
  return `${sym}${val.toFixed(0)}`;
}

/** Compact percent for chart axes (e.g. 85%). */
export function formatCompactPercent(val: number): string {
  return `${(val * 100).toFixed(0)}%`;
}

/** Return the right compact formatter for a DataFormat hint. */
export function axisFormatterFor(
  fmt: DataFormat | string | null | undefined,
): (val: number) => string {
  // Igualdad exacta no alcanza: `currency:CLP` caia en el formateador
  // de numeros y el eje perdia el simbolo entero.
  if (parseCurrencyFormat(fmt)) return (v: number) => formatCompactCurrency(v, fmt);
  if (fmt === "percent") return formatCompactPercent;
  return formatCompactNumber;
}

/** Full-number formatters for tooltips — show 1,234,567 not 1.2M. */
function fullNumber(val: number): string {
  return cachedNumberFormat(intlLocale(), { maximumFractionDigits: 0 }).format(val);
}
function fullCurrency(val: number, fmt?: DataFormat | string | null): string {
  const c = parseCurrencyFormat(fmt) ?? { code: "USD", declared: false };
  return cachedNumberFormat(intlLocale(), currencyIntlOpts(c)).format(val);
}
function fullPercent(val: number): string {
  return `${(val * 100).toFixed(1)}%`;
}

/** Return a full-precision formatter for chart tooltips. */
export function tooltipFormatterFor(
  fmt: DataFormat | string | null | undefined,
): (val: number) => string {
  if (parseCurrencyFormat(fmt)) return (v: number) => fullCurrency(v, fmt);
  if (fmt === "percent") return fullPercent;
  return fullNumber;
}

/**
 * Detect a serialized ``LocalizedString`` cell value (a JSON-shaped
 * string with at least ``en`` or ``es`` keys) and resolve it to the
 * locale-appropriate side. Some admin-authored table SQL emits row
 * values like ``'{"en": "Publications", "es": "Publicaciones"}'`` so
 * the bilingual label survives the engine layer (which only knows
 * how to pass scalars through). Without this guard the DataTable
 * would render the raw JSON string in a cell — the iter22 surface
 * of gating_executive's Fiscal Quarter Comparison table.
 */
function _resolveBilingualCell(value: string): string | null {
  if (value.length < 8 || !value.startsWith("{")) return null;
  if (!SUPPORTED_LOCALES.some((loc) => value.includes(`"${loc}"`))) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const obj = parsed as Record<string, unknown>;
    const en = typeof obj.en === "string" ? obj.en : "";
    const es = typeof obj.es === "string" ? obj.es : "";
    const pt = typeof obj.pt === "string" ? obj.pt : "";
    if (!en && !es && !pt) return null;
    const locale = getActiveLocale();
    if (locale === "en") return en || es || pt;
    if (locale === "es") return es || en || pt;
    return pt || en || es;
  } catch {
    return null;
  }
}

/** Full-precision cell value used by KPICard + DataTable. */
export function formatCellValue(
  value: unknown,
  format?: DataFormat | string | null,
): string {
  if (value === null || value === undefined) return "—";
  if (format === "text" || typeof value === "string") {
    const raw = String(value);
    const localized = _resolveBilingualCell(raw);
    return localized ?? raw;
  }
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  // La moneda se resuelve ANTES del switch: `currency:CLP` no puede
  // matchear un `case` literal, y con igualdad exacta caia en la rama
  // `default` — un precio sin simbolo ni separador de miles.
  const cur = parseCurrencyFormat(format);
  if (cur) {
    return cachedNumberFormat(intlLocale(), currencyIntlOpts(cur)).format(num);
  }
  switch (format) {
    case "percent": {
      // 0 decimals for whole percentages (100%, 80%), 1 decimal
      // otherwise (86.7%, 12.5%). Matches the legacy dashboard
      // convention from the portal.
      // The decimal separator follows the app language ("62,7%" in ES).
      const pct = num * 100;
      const digits = Number.isInteger(pct) ? 0 : 1;
      return `${cachedNumberFormat(intlLocale(), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(pct)}%`;
    }
    case "number":
      // El locale ACTIVO, igual que las fechas (que ya usaban
      // `intlLocale()` desde siempre). Que los numeros quedaran en
      // `en-US` mientras las fechas seguian el idioma era una
      // inconsistencia interna, no una decision: en español un
      // `279,990` se lee 279 con novecientos noventa milesimos.
      return cachedNumberFormat(intlLocale(), {
        maximumFractionDigits: 1,
      }).format(num);
    default:
      return cachedNumberFormat(intlLocale(), {}).format(Math.round(num));
  }
}

/** Coerce unknown to number (0 for non-numeric, NaN, ±Infinity).
 *
 * SVG charts in the bi-components library compute coordinates from
 * these numbers and produce NaN-laced attributes when nulls leak
 * through, e.g. ``<polyline points="NaN,30">``. Always falling back
 * to 0 keeps the geometry valid; charts that genuinely have empty
 * data render as "value=0" bars/points instead of crashing the SVG.
 */
export function toNumber(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** One RFC 4180 CSV field: quoted when it contains the separator, a
 *  double quote or a line break; inner quotes are doubled. */
export function csvField(str: string, sep: string): string {
  return str.includes(sep) || /["\r\n]/.test(str)
    ? `"${str.replace(/"/g, '""')}"`
    : str;
}
